/**
 * CustomerAuthModal — single shadcn Dialog that hosts 3 views: Login / Signup / Forgot.
 * Used to gate access to the calculator and as a manual auth entrypoint.
 */
import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2, Mail, Phone, User, Lock, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useCustomerAuth, formatApiError } from '../contexts/CustomerAuthContext';

const VIEWS = { LOGIN: 'login', SIGNUP: 'signup', FORGOT: 'forgot' };

// Google "G" logo — inline SVG so we don't add a new dependency
function GoogleLogo({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

function GoogleButton({ label = 'Continue with Google' }) {
  const { startGoogleSignIn } = useCustomerAuth();
  return (
    <button
      type="button"
      onClick={startGoogleSignIn}
      className="w-full h-11 rounded-md border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center gap-2 font-semibold text-slate-700 shadow-sm transition"
      data-testid="google-signin-btn"
    >
      <GoogleLogo />
      {label}
    </button>
  );
}

function OrDivider() {
  return (
    <div className="relative py-1">
      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
      <div className="relative flex justify-center"><span className="bg-white px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">or</span></div>
    </div>
  );
}

export default function CustomerAuthModal({ open, onClose, defaultView = VIEWS.SIGNUP, title, subtitle }) {
  const [view, setView] = useState(defaultView);
  useEffect(() => { if (open) setView(defaultView); }, [open, defaultView]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="w-[95vw] max-w-md p-0 gap-0 rounded-2xl overflow-hidden" data-testid="customer-auth-modal">
        <DialogTitle className="sr-only">EntoRid account</DialogTitle>
        <DialogDescription className="sr-only">Login, signup, or recover your EntoRid account</DialogDescription>
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white px-6 py-5">
          <h2 className="text-xl font-bold">{title || 'Welcome to EntoRid'}</h2>
          <p className="text-emerald-100 text-sm mt-1">{subtitle || 'Create an account to see your live price quote'}</p>
        </div>
        <div className="p-6">
          {view === VIEWS.LOGIN && <LoginView onSwitch={setView} onClose={onClose} />}
          {view === VIEWS.SIGNUP && <SignupView onSwitch={setView} onClose={onClose} />}
          {view === VIEWS.FORGOT && <ForgotView onSwitch={setView} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// LOGIN
// ============================================================
function LoginView({ onSwitch, onClose }) {
  const { login } = useCustomerAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) { setErr('Please fill both fields'); return; }
    setBusy(true); setErr('');
    try {
      await login(identifier.trim(), password);
      onClose?.();
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <GoogleButton label="Continue with Google" />
      <OrDivider />
      <div>
        <Label className="text-xs font-semibold text-slate-600">Email or Mobile</Label>
        <div className="relative mt-1">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="you@example.com or 9876543210"
            className="pl-9 h-11"
            autoComplete="username"
            data-testid="login-identifier"
            required
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold text-slate-600">Password</Label>
          <button type="button" onClick={() => onSwitch(VIEWS.FORGOT)} className="text-xs font-semibold text-emerald-700 hover:underline" data-testid="forgot-link">
            Forgot password?
          </button>
        </div>
        <div className="relative mt-1">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type={showPw ? 'text' : 'password'}
            placeholder="Your password"
            className="pl-9 pr-10 h-11"
            autoComplete="current-password"
            data-testid="login-password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPw(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            aria-label={showPw ? 'Hide password' : 'Show password'}
            data-testid="login-toggle-password"
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>
      {err && <ErrorBanner msg={err} />}
      <Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="login-submit">
        {busy ? <Loader2 size={16} className="animate-spin" /> : 'Log in'}
      </Button>
      <div className="text-center text-sm text-slate-600">
        New here?{' '}
        <button type="button" onClick={() => onSwitch(VIEWS.SIGNUP)} className="font-bold text-emerald-700 hover:underline" data-testid="goto-signup">
          Create an account
        </button>
      </div>
    </form>
  );
}

// ============================================================
// SIGNUP
// ============================================================
function SignupView({ onSwitch, onClose }) {
  const { signup } = useCustomerAuth();
  const [form, setForm] = useState({ name: '', email: '', mobile: '', city: '', password: '', consent_marketing: true });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) { setErr('Please enter your full name'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) { setErr('Please enter a valid email'); return; }
    const mobile10 = String(form.mobile).replace(/\D/g, '').slice(-10);
    if (mobile10.length !== 10) { setErr('Please enter a 10-digit mobile number'); return; }
    if (form.password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    setBusy(true); setErr('');
    try {
      await signup({ ...form, mobile: mobile10 });
      onClose?.();
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
      <GoogleButton label="Sign up with Google" />
      <OrDivider />
      <div>
        <Label className="text-xs font-semibold text-slate-600">Full Name</Label>
        <div className="relative mt-1">
          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={form.name} onChange={update('name')} placeholder="e.g. Rajesh Sharma" className="pl-9 h-11" data-testid="signup-name" required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold text-slate-600">Mobile</Label>
          <div className="relative mt-1">
            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={form.mobile} onChange={update('mobile')} placeholder="9876543210" maxLength={10} inputMode="numeric" className="pl-9 h-11" data-testid="signup-mobile" required />
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-600">City</Label>
          <Input value={form.city} onChange={update('city')} placeholder="Dehradun" className="h-11" data-testid="signup-city" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-600">Email</Label>
        <div className="relative mt-1">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" className="pl-9 h-11" data-testid="signup-email" required />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold text-slate-600">Password</Label>
        <div className="relative mt-1">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={form.password}
            onChange={update('password')}
            type={showPw ? 'text' : 'password'}
            placeholder="Min 6 characters"
            className="pl-9 pr-10 h-11"
            autoComplete="new-password"
            data-testid="signup-password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPw(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            aria-label={showPw ? 'Hide password' : 'Show password'}
            data-testid="signup-toggle-password"
          >
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">Your password is encrypted with bcrypt and never visible to us in plain text.</p>
      </div>
      <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
        <input
          type="checkbox"
          checked={form.consent_marketing}
          onChange={(e) => setForm({ ...form, consent_marketing: e.target.checked })}
          className="mt-0.5"
          data-testid="signup-consent"
        />
        <span>Send me service reminders &amp; member-only offers via email/SMS.</span>
      </label>
      {err && <ErrorBanner msg={err} />}
      <Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="signup-submit">
        {busy ? <Loader2 size={16} className="animate-spin" /> : 'Create account'}
      </Button>
      <div className="text-center text-sm text-slate-600">
        Already have an account?{' '}
        <button type="button" onClick={() => onSwitch(VIEWS.LOGIN)} className="font-bold text-emerald-700 hover:underline" data-testid="goto-login">
          Log in
        </button>
      </div>
    </form>
  );
}

// ============================================================
// FORGOT (2 steps: request OTP → reset)
// ============================================================
function ForgotView({ onSwitch }) {
  const { forgot, reset, login } = useCustomerAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPw, setNewPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { setErr('Please enter a valid email'); return; }
    setBusy(true); setErr('');
    try {
      const r = await forgot(email);
      setMsg(r.message || 'OTP sent. Please check your inbox.');
      setStep(2);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setErr('Enter the 6-digit OTP from your email'); return; }
    if (newPw.length < 6) { setErr('New password must be at least 6 characters'); return; }
    setBusy(true); setErr('');
    try {
      await reset(email, otp, newPw);
      // Auto-login on success
      await login(email, newPw);
      // Switch to login view (will be closed by parent after auth-state change is consumed)
      onSwitch(VIEWS.LOGIN);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="forgot-form">
      <button type="button" onClick={() => onSwitch(VIEWS.LOGIN)} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="forgot-back">
        <ArrowLeft size={14} /> Back to login
      </button>
      {step === 1 && (
        <form onSubmit={requestOtp} className="space-y-3">
          <h3 className="font-bold text-slate-900">Reset your password</h3>
          <p className="text-sm text-slate-600">Enter your registered email — we&apos;ll send a 6-digit OTP valid for 10 minutes.</p>
          <div>
            <Label className="text-xs font-semibold text-slate-600">Email</Label>
            <div className="relative mt-1">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-9 h-11" data-testid="forgot-email" required />
            </div>
          </div>
          {err && <ErrorBanner msg={err} />}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="forgot-send-otp">
            {busy ? <Loader2 size={16} className="animate-spin" /> : 'Send OTP'}
          </Button>
        </form>
      )}
      {step === 2 && (
        <form onSubmit={doReset} className="space-y-3">
          <h3 className="font-bold text-slate-900">Enter OTP &amp; new password</h3>
          {msg && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2 inline-flex items-start gap-1.5"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" /> {msg}</div>}
          <div>
            <Label className="text-xs font-semibold text-slate-600">6-digit OTP</Label>
            <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••••" maxLength={6} inputMode="numeric" className="h-11 text-center tracking-[0.5em] font-bold text-lg" data-testid="forgot-otp" required />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600">New Password</Label>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                type={showPw ? 'text' : 'password'}
                placeholder="Min 6 characters"
                className="pl-9 pr-10 h-11"
                autoComplete="new-password"
                data-testid="forgot-new-password"
                required
              />
              <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="forgot-toggle-password">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {err && <ErrorBanner msg={err} />}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 font-bold" data-testid="forgot-submit-reset">
            {busy ? <Loader2 size={16} className="animate-spin" /> : 'Reset password'}
          </Button>
          <button type="button" onClick={() => setStep(1)} className="block mx-auto text-xs text-slate-500 hover:underline" data-testid="forgot-resend">Didn&apos;t get it? Resend OTP</button>
        </form>
      )}
    </div>
  );
}

function ErrorBanner({ msg }) {
  return (
    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 inline-flex items-start gap-1.5" data-testid="auth-error">
      <AlertCircle size={14} className="mt-0.5 flex-shrink-0" /> {msg}
    </div>
  );
}
